import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';

/**
 * Hook to manage global workspace data (shared across all weeks)
 * 
 * @param {string} workspaceId - Workspace ID
 * @returns {Object} Global data and update functions
 */
export const useGlobalData = (workspaceId) => {
  console.log('useGlobalData hook called:', workspaceId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Global data (shared across weeks)
  const [bills, setBills] = useState([]);
  const [skippedBills, setSkippedBills] = useState({});

  // Subscribe to global data changes
  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    const unsubscribers = [];

    // Subscribe to bills (using existing data structure)
    const billsUnsubscribe = onSnapshot(
      doc(db, `workspaces/${workspaceId}/data/bills`),
      (doc) => {
        console.log('Global bills update:', doc.exists() ? 'exists' : 'not found');
        if (doc.exists()) {
          setBills(doc.data().bills || []);
        } else {
          setBills([]);
        }
      },
      (error) => {
        console.error('Error loading bills:', error);
        setError(error.message);
      }
    );
    unsubscribers.push(billsUnsubscribe);

    // Subscribe to skipped bills (using existing data structure)
    const skippedUnsubscribe = onSnapshot(
      doc(db, `workspaces/${workspaceId}/data/skippedBills`),
      (doc) => {
        console.log('Global skipped bills update:', doc.exists() ? 'exists' : 'not found');
        if (doc.exists()) {
          setSkippedBills(doc.data().skippedBills || {});
        } else {
          setSkippedBills({});
        }
      },
      (error) => {
        console.error('Error loading skipped bills:', error);
        setError(error.message);
      }
    );
    unsubscribers.push(skippedUnsubscribe);

    setLoading(false);
    console.log('Global data subscriptions set up');

    // Cleanup
    return () => {
      console.log('Cleaning up global data subscriptions');
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [workspaceId]);

  // Update functions
  const updateBills = useCallback(async (newBills) => {
    try {
      await setDoc(doc(db, `workspaces/${workspaceId}/data/bills`), {
        bills: newBills,
        lastUpdated: new Date()
      });
      console.log('Bills updated successfully');
    } catch (error) {
      console.error('Error updating bills:', error);
      setError(error.message);
    }
  }, [workspaceId]);

  const updateSkippedBills = useCallback(async (newSkippedBills) => {
    try {
      await setDoc(doc(db, `workspaces/${workspaceId}/data/skippedBills`), {
        skippedBills: newSkippedBills,
        lastUpdated: new Date()
      });
      console.log('Skipped bills updated successfully');
    } catch (error) {
      console.error('Error updating skipped bills:', error);
      setError(error.message);
    }
  }, [workspaceId]);

  return {
    loading,
    error,
    bills,
    skippedBills,
    updateBills,
    updateSkippedBills
  };
};